import { FC } from "react";

import { Box, Stack } from "@mui/material";
import { StaticVariableTag } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { AutomatedCommunicationCard } from ".././components/AutomatedCommunicationCard";
import { SendAutomatedEmailTestButton } from "../components/SendAutomatedEmailTestButton";
import { AutomatedCommunicationsFormData, RowKey } from "../types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ExtraWysiwygVariable, StaticVariableLabels } from "@/components/OutlinedTextFieldWysiwyg/type";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { Translations } from "@/public/@types/translations";

type DashboardKey = keyof Translations["dashboard"] & string;

type MembershipKind = "expiring" | "auto-renewal";

interface MembershipRenewalCardProps {
  kind: MembershipKind;
  openRow: RowKey | null;
  onToggleOpen: (row: RowKey) => void;
  defaultContent: string;
}

const KIND_CONFIG: Record<
  MembershipKind,
  {
    fieldKey: "expiringMembership" | "autoRenewal";
    rowKey: RowKey;
    label: DashboardKey;
    suffixLabel: DashboardKey;
    amplitudeEmailType: string;
    extraVariableKeys: string[];
    dataTestPrefix: string;
    showFootnote: boolean;
  }
> = {
  expiring: {
    fieldKey: "expiringMembership",
    rowKey: "expiring-membership",
    label: "membership.expiringMembershipReminder.label",
    suffixLabel: "membership.renewalEmail.daysBeforeExpiration",
    amplitudeEmailType: "Yearly Membership Expiration Reminder",
    extraVariableKeys: ["memberFirstName", "memberLastName", "membershipType", "expiryDate", "paymentMethod"],
    dataTestPrefix: "live-form-editor-expiring-membership",
    showFootnote: true,
  },
  "auto-renewal": {
    fieldKey: "autoRenewal",
    rowKey: "auto-renewal",
    label: "membership.autoRenewalReminder.label",
    suffixLabel: "membership.renewalEmail.daysBeforeRenewal",
    amplitudeEmailType: "Auto-Renewal Notification",
    extraVariableKeys: [
      "memberFirstName",
      "memberLastName",
      "membershipType",
      "expiryDate",
      "renewalAmount",
      "paymentMethod",
    ],
    dataTestPrefix: "live-form-editor-auto-renewal",
    showFootnote: false,
  },
};

const VARIABLE_TAGS: Record<
  string,
  { tag: string; labelKey: DashboardKey; tooltipKey?: DashboardKey; dividerBefore?: boolean }
> = {
  memberFirstName: {
    tag: "{{Member first name}}",
    labelKey: "membership.renewalEmail.variable.memberFirstName",
    tooltipKey: "membership.renewalEmail.variable.memberTooltip",
    dividerBefore: true,
  },
  memberLastName: {
    tag: "{{Member last name}}",
    labelKey: "membership.renewalEmail.variable.memberLastName",
    tooltipKey: "membership.renewalEmail.variable.memberTooltip",
  },
  membershipType: {
    tag: "{{Membership type}}",
    labelKey: "membership.renewalEmail.variable.membershipType",
    dividerBefore: true,
  },
  expiryDate: {
    tag: "{{Expiry date}}",
    labelKey: "membership.renewalEmail.variable.expiryDate",
  },
  renewalAmount: {
    tag: "{{Renewal amount}}",
    labelKey: "membership.renewalEmail.variable.renewalAmount",
  },
  paymentMethod: {
    tag: "{{Payment method}}",
    labelKey: "membership.renewalEmail.variable.paymentMethod",
  },
};

export const MembershipRenewalCard: FC<MembershipRenewalCardProps> = ({
  kind,
  openRow,
  onToggleOpen,
  defaultContent,
}) => {
  const { t } = useTranslate();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const form = useFormContext<AutomatedCommunicationsFormData>();
  const config = KIND_CONFIG[kind];

  const enabled = useWatch({ control: form.control, name: `${config.fieldKey}.enabled` });
  const delayDays = useWatch({ control: form.control, name: `${config.fieldKey}.delayDays` });

  const extraVariables: ExtraWysiwygVariable[] = config.extraVariableKeys.map((key) => ({
    tag: VARIABLE_TAGS[key].tag,
    label: t("dashboard", VARIABLE_TAGS[key].labelKey),
    ...(VARIABLE_TAGS[key].tooltipKey ? { tooltip: t("dashboard", VARIABLE_TAGS[key].tooltipKey) } : {}),
    ...(VARIABLE_TAGS[key].dividerBefore ? { dividerBefore: true } : {}),
  }));

  const staticVariableLabels: StaticVariableLabels = {
    [StaticVariableTag.FirstName]: t("dashboard", "membership.renewalEmail.variable.buyerFirstName"),
    [StaticVariableTag.LastName]: t("dashboard", "membership.renewalEmail.variable.buyerLastName"),
  };

  return (
    <AutomatedCommunicationCard<AutomatedCommunicationsFormData>
      label={t("dashboard", config.label)}
      control={form.control}
      errors={form.formState.errors}
      dataEmailType={config.rowKey}
      collapse={{ isOpen: openRow === config.rowKey, onToggle: () => onToggleOpen(config.rowKey) }}
      fields={{ subject: `${config.fieldKey}.subject`, content: `${config.fieldKey}.content` }}
      enabled={Boolean(enabled)}
      onEnabledChange={(next) => {
        form.setValue(`${config.fieldKey}.enabled`, next, { shouldDirty: true });
        logUserActionEvent(next ? AmplitudeEvents.AutomaticEmailEnabled : AmplitudeEvents.AutomaticEmailDisabled, {
          email_type: config.amplitudeEmailType,
        });
      }}
      chip={{
        label: t("dashboard", "membership.renewalEmail.chipLabel.daysBefore", { count: delayDays ?? 0 }),
        showWhen: "enabled",
      }}
      offset={{
        name: `${config.fieldKey}.delayDays`,
        suffixLabel: t("dashboard", config.suffixLabel, { count: delayDays ?? 0 }),
        onBlur: () =>
          logUserActionEvent(AmplitudeEvents.AutomaticEmailDateChanged, { email_type: config.amplitudeEmailType }),
      }}
      body={{ defaultValue: defaultContent, extraVariables, staticVariableLabels }}
      onBlur={() =>
        logUserActionEvent(AmplitudeEvents.AutomaticEmailCustomized, { email_type: config.amplitudeEmailType })
      }
      footnote={
        <Stack marginTop={2} gap={2}>
          {config.showFootnote ? (
            <DashboardTypography variant="caption" sx={(theme) => ({ color: theme.palette.text.neutral.quiet })}>
              {t("dashboard", "membership.renewalEmail.note.alreadyRenewed")}
            </DashboardTypography>
          ) : null}
          <Box display="flex" justifyContent="flex-start">
            <SendAutomatedEmailTestButton<AutomatedCommunicationsFormData>
              emailType="membership-renewal"
              subjectFieldName={`${config.fieldKey}.subject`}
              contentFieldName={`${config.fieldKey}.content`}
            />
          </Box>
        </Stack>
      }
      dataTestPrefix={config.dataTestPrefix}
    />
  );
};
